import { ApiAuthenticateRequestFromJSON, ApiAuthenticateResponse } from '@ibe/api';
import { Credentials as Creds, ValidationError } from '@ibe/services';
import { observer } from 'mobx-react';
import React, { useState, FunctionComponent, JSX } from 'react';
import * as yup from 'yup';
import fallback from '../../../_Translations/generated/login.en.t.json';
import Keys from '../../../_Translations/generated/login.en.t.json.keys';
import useApi from '../../Hooks/useApi';
import useAuth from '../../Hooks/useAuth';
import useTranslation from '../../Hooks/useTranslation';

export class Credentials extends Creds {}

export type CredentialKey = keyof Credentials;
export type FormErrors = Partial<Record<CredentialKey, string[]>>;

export type LoginFormProps = {
  children?: never;
  isDisabled?: boolean | undefined;
  hasResetPassword?: boolean;
  mandator?: string;
  initialValues?: Partial<Credentials>;
  mainError?: string;
  fieldErrors?: FormErrors;
  withCaptcha?: boolean;
  onSubmit: (values: Credentials) => Promise<void>;
};

type LoginProps = {
  onSuccess: (token: string) => Promise<void>;
  onPasswordChangeRequired: (agency: string, login: string, token: string) => void;
  Form: FunctionComponent<LoginFormProps>;
  loggedIn?: () => JSX.Element;
  withCaptcha?: boolean;
} & Omit<LoginFormProps, 'onSubmit'>;

export const Login = observer(
  (props: LoginProps): JSX.Element => {
    const { t } = useTranslation('login', fallback, Keys);
    const errorMessages = {
      merchantRequired: t(Keys.merchantRequiredError),
      passwordRequired: t(Keys.passwordRequiredError),
      userRequired: t(Keys.userRequiredError),
      tokenRequired: t(Keys.captchaRequiredError)
    };

    const { loggedIn, Form, onPasswordChangeRequired, onSuccess, withCaptcha } = props;
    const api = useApi();
    const { authService } = useAuth();

    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [mainError, setMainError] = useState<string | undefined>(undefined);

    const validate = async (credentials: Credentials): Promise<Credentials> => {
      const credentialsSchema = withCaptcha
        ? yup.object({
            merchant: yup.string().required(errorMessages.merchantRequired),
            password: yup.string().required(errorMessages.passwordRequired),
            user: yup.string().required(errorMessages.userRequired),
            token: yup.string().required(errorMessages.tokenRequired)
          })
        : yup.object({
            merchant: yup.string().required(errorMessages.merchantRequired),
            password: yup.string().required(errorMessages.passwordRequired),
            user: yup.string().required(errorMessages.userRequired)
          });
      return credentialsSchema.validate(credentials, { abortEarly: false });
    };

    const getFieldErrors = async (
      credentials: Credentials
    ): Promise<{ [key: string]: string[] }> => {
      const errors: { [key: string]: string[] } = {};

      await validate(credentials).catch((e: ValidationError) =>
        e.inner.forEach((ee: ValidationError): void => {
          if (ee && ee.path) {
            errors[ee.path] = ee.errors;
          }
        })
      );

      return errors;
    };
    const onSubmit = async (credentials: Credentials): Promise<void> => {
      setMainError('');

      const errors = await getFieldErrors(credentials);
      const hasErrors = Object.keys(errors).length > 0;
      setFieldErrors(errors);

      if (!hasErrors) {
        try {
          const authenticationRequest = ApiAuthenticateRequestFromJSON(credentials);
          const response: ApiAuthenticateResponse = await api.authenticate(authenticationRequest);

          if (response.changePasswordToken) {
            onPasswordChangeRequired(
              credentials.merchant,
              credentials.user,
              response.changePasswordToken
            );
          } else {
            authService.authenticate(response.token);
            await onSuccess(response.token);
          }
        } catch (e) {
          switch ((e as Response).status) {
            case 401:
            case 400: {
              setMainError(t(Keys.authError));
              break;
            }
            case 500: {
              setMainError(t(Keys.serverError));
              break;
            }
            default: {
              setMainError(t(Keys.unknownError));
            }
          }
        }
      }
    };

    if (loggedIn && authService.state.isLoggedIn) {
      return <>{loggedIn()}</>;
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Form {...props} onSubmit={onSubmit} fieldErrors={fieldErrors} mainError={mainError} />;
  }
);
